import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import './Analytics.css';

const QUARTERS_TO_SHOW = 4;
const CLOSED_STATUS_SET = new Set(['closed', 'solved', 'resolved', 'completed']);

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getQuarterRanges = () => {
  const ranges = [];
  const referenceDate = new Date();
  let year = referenceDate.getFullYear();
  let quarterIndex = Math.floor(referenceDate.getMonth() / 3);

  for (let i = 0; i < QUARTERS_TO_SHOW; i += 1) {
    const startMonth = quarterIndex * 3;
    const start = new Date(year, startMonth, 1, 0, 0, 0, 0);
    const end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);

    ranges.push({
      label: `Q${quarterIndex + 1} ${year}`,
      start,
      end
    });

    quarterIndex -= 1;
    if (quarterIndex < 0) {
      quarterIndex = 3;
      year -= 1;
    }
  }

  return ranges.reverse();
};

const buildTicketQuarterStats = (tickets = []) => {
  const quarters = getQuarterRanges().map((range) => ({
    ...range,
    opened: 0,
    closed: 0
  }));

  tickets.forEach((ticket) => {
    const createdAt = parseDate(ticket.created_at || ticket.createdAt || ticket.CreatedDateTime);
    const updatedAt = parseDate(
      ticket.updated_at || ticket.updatedAt || ticket.closed_at || ticket.closedAt
    );
    const status = String(ticket.status || ticket.Status || '').toLowerCase();

    if (createdAt) {
      quarters.forEach((quarter) => {
        if (createdAt >= quarter.start && createdAt <= quarter.end) {
          quarter.opened += 1;
        }
      });
    }

    if (CLOSED_STATUS_SET.has(status)) {
      const effectiveClosedDate = updatedAt || createdAt;
      if (effectiveClosedDate) {
        quarters.forEach((quarter) => {
          if (effectiveClosedDate >= quarter.start && effectiveClosedDate <= quarter.end) {
            quarter.closed += 1;
          }
        });
      }
    }
  });

  const maxValue = quarters.reduce(
    (max, quarter) => Math.max(max, quarter.opened, quarter.closed),
    0
  );

  return {
    quarters,
    maxValue
  };
};

const Analytics = () => {
  const { selectedTenant, authenticatedFetch, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    unusedLicenses: [],
    underlicensedProducts: [],
    cloudfactorySubscriptions: [],
    deviceLifecycle: [],
    userTimeline: []
  });
  const [cleanupSuggestions, setCleanupSuggestions] = useState({
    onedrive: [],
    mailbox: []
  });
  const [creatingTicketFor, setCreatingTicketFor] = useState(null);
  const [ticketCreateFeedback, setTicketCreateFeedback] = useState({});
  const [ticketQuarterStats, setTicketQuarterStats] = useState({
    quarters: [],
    maxValue: 0
  });
  const [ticketStatsError, setTicketStatsError] = useState(null);

  const fetchTicketQuarterStats = useCallback(
    async (tenantName) => {
      if (!tenantName) return;
      try {
        setTicketStatsError(null);
        const params = new URLSearchParams({
          tenant: tenantName,
          sinceDays: '450'
        });
        const response = await authenticatedFetch(`/api/tickets?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const stats = buildTicketQuarterStats(Array.isArray(data.tickets) ? data.tickets : []);
        setTicketQuarterStats(stats);
      } catch (statsError) {
        console.error('Error fetching ticket stats:', statsError);
        setTicketStatsError('Unable to load ticket volume data right now.');
        setTicketQuarterStats({ quarters: [], maxValue: 0 });
      }
    },
    [authenticatedFetch]
  );

  const fetchAnalyticsData = useCallback(async () => {
    if (!selectedTenant || !selectedTenant.name) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setTicketStatsError(null);

      const response = await authenticatedFetch(`/api/analytics?tenant=${selectedTenant.name}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Fetch user timeline data
      const timelineResponse = await authenticatedFetch(`/api/users/timeline?tenant=${selectedTenant.name}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (timelineResponse.ok) {
        const timelineData = await timelineResponse.json();
        setAnalyticsData({
          ...data,
          userTimeline: timelineData.success ? timelineData.timeline : []
        });
      } else {
        setAnalyticsData({
          ...data,
          userTimeline: []
        });
      }

      // Fetch user cleanup suggestions
      const cleanupResponse = await authenticatedFetch(`/api/users/cleanup-suggestions?tenant=${selectedTenant.name}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (cleanupResponse.ok) {
        const cleanupData = await cleanupResponse.json();
        setCleanupSuggestions({
          onedrive: cleanupData.onedriveSuggestions || [],
          mailbox: cleanupData.mailboxSuggestions || []
        });
      } else {
        setCleanupSuggestions({
          onedrive: [],
          mailbox: []
        });
      }

      await fetchTicketQuarterStats(selectedTenant.name);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch, fetchTicketQuarterStats]);

  useEffect(() => {
    if (selectedTenant && selectedTenant.name) {
      fetchAnalyticsData();
    }
  }, [selectedTenant, fetchAnalyticsData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysSinceActivity = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    const activityDate = new Date(dateString);
    const diffTime = today - activityDate;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getRequester = useCallback(() => {
    const candidates = [
      user?.email,
      user?.Email,
      user?.upn,
      user?.Username,
      user?.username
    ];
    const identity = candidates.find((v) => typeof v === 'string' && v.trim());
    return identity ? identity.trim() : 'Portal User';
  }, [user]);

  const createCleanupTicket = useCallback(
    async (cleanupUser, type) => {
      const upn = cleanupUser.userPrincipalName || 'Unknown';
      const key = `${type}-${upn}`;
      if (!selectedTenant?.name) return;

      setCreatingTicketFor(key);
      setTicketCreateFeedback((prev) => ({ ...prev, [key]: null }));

      const daysSince =
        type === 'onedrive'
          ? getDaysSinceActivity(cleanupUser.OnedriveLastActivity)
          : getDaysSinceActivity(cleanupUser.MailboxLastActivity);
      const lastActivity =
        type === 'onedrive'
          ? formatDate(cleanupUser.OnedriveLastActivity)
          : formatDate(cleanupUser.MailboxLastActivity);
      const activityType = type === 'onedrive' ? 'OneDrive' : 'Mailbox';
      const daysText = daysSince !== null ? `${daysSince} days` : 'over 60 days';

      const details = [
        `User (UPN): ${upn}`,
        '',
        `This user has had no ${activityType} activity for ${daysText} (last activity: ${lastActivity}).`,
        'Please investigate why the account is no longer in use and consider disabling the account if appropriate.'
      ].join('\n');

      const payload = {
        tenant: selectedTenant.name,
        requester: getRequester(),
        subject: `User cleanup: ${upn} - Review inactive ${activityType} user`,
        details
      };

      try {
        const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tickets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          setTicketCreateFeedback((prev) => ({
            ...prev,
            [key]: {
              success: true,
              message: data.message || 'Ticket created successfully',
              ticketUrl: data.ticketUrl
            }
          }));
        } else {
          setTicketCreateFeedback((prev) => ({
            ...prev,
            [key]: {
              success: false,
              message: data.error || 'Failed to create ticket. Please try again.'
            }
          }));
        }
      } catch (err) {
        console.error('Error creating cleanup ticket:', err);
        setTicketCreateFeedback((prev) => ({
          ...prev,
          [key]: { success: false, message: 'Network error. Please try again.' }
        }));
      } finally {
        setCreatingTicketFor(null);
      }
    },
    [selectedTenant, authenticatedFetch, getRequester]
  );

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getLifecycleStatus = (daysUntilExpiry) => {
    if (daysUntilExpiry === null) return 'unknown';
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'critical';
    if (daysUntilExpiry <= 90) return 'warning';
    if (daysUntilExpiry <= 180) return 'attention';
    return 'good';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'expired': return '#ff4444';
      case 'critical': return '#ff8800';
      case 'warning': return '#ffaa00';
      case 'attention': return '#ffdd00';
      case 'good': return '#44ff44';
      default: return '#888888';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'expired': return 'Expired';
      case 'critical': return 'Critical (≤30 days)';
      case 'warning': return 'Warning (≤90 days)';
      case 'attention': return 'Attention (≤180 days)';
      case 'good': return 'Good (>180 days)';
      default: return 'Unknown';
    }
  };

  const quarterStats = ticketQuarterStats.quarters || [];
  const hasQuarterData = quarterStats.some((quarter) => quarter.opened > 0 || quarter.closed > 0);

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2>Analytics Dashboard</h2>
        </div>
        <div className="loading">Loading analytics data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2>Analytics Dashboard</h2>
        </div>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  if (!selectedTenant || !selectedTenant.name) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2>Analytics Dashboard</h2>
        </div>
        <div className="no-tenant">Please select a tenant to view analytics</div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>Analytics Dashboard</h2>
        <p>License optimization and device lifecycle insights</p>
      </div>

      <div className="analytics-grid">
        <div className="analytics-section ticket-quarterly">
          <div className="section-header">
            <h3>Ticket Volume (Last 4 Quarters)</h3>
            <p>Comparison of opened vs closed tickets per quarter</p>
          </div>
          <div className="section-content">
            {ticketStatsError ? (
              <div className="inline-error">{ticketStatsError}</div>
            ) : !hasQuarterData ? (
              <div className="no-data">No ticket activity recorded in the last 4 quarters</div>
            ) : (
              <div className="ticket-quarter-chart">
                <div className="ticket-quarter-chart__body">
                  <div className="ticket-quarter-chart__y-axis">
                    {(() => {
                      const ticks = [];
                      const maxValue = ticketQuarterStats.maxValue || 0;
                      if (maxValue === 0) {
                        return <span className="ticket-quarter-chart__y-tick">0</span>;
                      }
                      const step = Math.max(1, Math.ceil(maxValue / 4));
                      for (let value = 0; value <= maxValue; value += step) {
                        ticks.push(value);
                      }
                      if (ticks[ticks.length - 1] !== maxValue) {
                        ticks.push(maxValue);
                      }
                      return ticks.map((value) => (
                        <span key={value} className="ticket-quarter-chart__y-tick">
                          {value}
                        </span>
                      ));
                    })()}
                  </div>
                  <div className="ticket-quarter-chart__groups">
                    {quarterStats.map((quarter) => {
                      const maxValue = ticketQuarterStats.maxValue || 0;
                      const openedHeight = maxValue > 0 ? (quarter.opened / maxValue) * 100 : 0;
                      const closedHeight = maxValue > 0 ? (quarter.closed / maxValue) * 100 : 0;
                      const startLabel = quarter.start
                        ? quarter.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '';
                      const endLabel = quarter.end
                        ? quarter.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '';
                      return (
                        <div key={quarter.label} className="ticket-quarter-chart__group">
                          <div className="ticket-quarter-chart__bars">
                            <div
                              className="ticket-quarter-chart__bar open"
                              style={{ height: `${openedHeight}%` }}
                              title={`${quarter.opened} opened tickets (${quarter.label})`}
                            >
                              <span className="ticket-quarter-chart__bar-value">{quarter.opened}</span>
                            </div>
                            <div
                              className="ticket-quarter-chart__bar closed"
                              style={{ height: `${closedHeight}%` }}
                              title={`${quarter.closed} closed tickets (${quarter.label})`}
                            >
                              <span className="ticket-quarter-chart__bar-value">{quarter.closed}</span>
                            </div>
                          </div>
                          <div className="ticket-quarter-chart__label">{quarter.label}</div>
                          <div className="ticket-quarter-chart__range-hint">
                            {startLabel && endLabel ? `${startLabel} - ${endLabel}` : 'No date data'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="ticket-quarter-chart__legend">
                  <div className="legend-item">
                    <span className="ticket-quarter-chart__swatch open"></span>
                    <span>Opened</span>
                  </div>
                  <div className="legend-item">
                    <span className="ticket-quarter-chart__swatch closed"></span>
                    <span>Closed</span>
                  </div>
                </div>
                {quarterStats.length > 0 && (
                  <div className="ticket-quarter-chart__footnote">
                    Data covers {quarterStats[0].label} – {quarterStats[quarterStats.length - 1].label}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Unused Licenses Section */}
        <div className="analytics-section">
          <div className="section-header">
            <h3>Unused Licenses</h3>
            <p>Potential cost savings from unused software licenses</p>
          </div>
          <div className="section-content">
            {analyticsData.unusedLicenses.length === 0 ? (
              <div className="no-data">No unused licenses found</div>
            ) : (
              <div className="licenses-grid">
                {analyticsData.unusedLicenses.map((license, index) => (
                  <div
                    key={index}
                    className="license-card unused"
                    onClick={() => {
                      // Navigate to License Grid in grouped (collapsed) mode by default
                      const evt = new CustomEvent('openLicenseGrid', { detail: { sku: license.skuPartNumber } });
                      window.dispatchEvent(evt);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="license-name">{license.name}</div>
                    {license.skuPartNumber && (
                      <div className="license-sku">SKU: {license.skuPartNumber}</div>
                    )}
                    {license.accountName && (
                      <div className="license-account">Account: {license.accountName}</div>
                    )}
                    <div className="license-details">
                      <span className="license-count">Available: {license.available}</span>
                      <span className="license-total">Total: {license.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Underlicensed Products Section */}
        <div className="analytics-section">
          <div className="section-header">
            <h3>Underlicensed Products</h3>
            <p>Products that need additional licenses for compliance</p>
          </div>
          <div className="section-content">
            {analyticsData.underlicensedProducts.length === 0 ? (
              <div className="no-data">All products are properly licensed</div>
            ) : (
              <div className="licenses-grid">
                {analyticsData.underlicensedProducts.map((product, index) => (
                  <div key={index} className="license-card underlicensed">
                    <div className="license-name">{product.name}</div>
                    {product.skuPartNumber && (
                      <div className="license-sku">SKU: {product.skuPartNumber}</div>
                    )}
                    {product.accountName && (
                      <div className="license-account">Account: {product.accountName}</div>
                    )}
                    <div className="license-details">
                      <span className="license-shortage">Shortage: {Math.abs(product.consumed)}</span>
                      <span className="license-total">Total: {product.total}</span>
                    </div>
                    <div className="compliance-status">
                      Status: Non-compliant
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cloudfactory Subscriptions Section */}
        <div className="analytics-section subscriptions">
          <div className="section-header">
            <h3>Active Subscriptions</h3>
            <p>Subscription billing information</p>
          </div>
          <div className="section-content">
            {analyticsData.cloudfactorySubscriptions.length === 0 ? (
              <div className="no-data">No cloudfactory subscription data available</div>
            ) : (
              <div className="subscriptions-grid">
                {analyticsData.cloudfactorySubscriptions.map((subscription, index) => (
                  <div 
                    key={index} 
                    className="subscription-card"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Extract SKU from subscription name (text before opening parenthesis)
                      const subscriptionName = subscription.subscriptionName || subscription.name || '';
                      const skuFromName = subscriptionName.split('(')[0].trim();
                      
                      console.log('Subscription clicked:', subscription.subscriptionName);
                      console.log('Extracted SKU for filtering:', skuFromName);
                      
                      // Navigate to License Grid with extracted SKU
                      if (skuFromName && skuFromName !== 'Unknown Subscription') {
                        console.log('Dispatching event with extracted SKU:', skuFromName);
                        const evt = new CustomEvent('openLicenseGrid', { detail: { sku: skuFromName } });
                        window.dispatchEvent(evt);
                      } else {
                        console.log('Could not extract valid SKU from subscription name');
                        alert('This subscription does not have a valid name for license filtering.');
                      }
                    }}
                    style={{ 
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 10,
                      backgroundColor: 'rgba(100, 181, 246, 0.2)',
                      border: '2px solid rgba(100, 181, 246, 0.5)'
                    }}
                  >
                    <div className="subscription-name">
                      {subscription.subscriptionName}
                      <span className="clickable-indicator"> 🔗</span>
                    </div>
                    <div className="subscription-details">
                      {subscription.billingCycle && (
                        <div className="subscription-billing">Billing: {subscription.billingCycle}</div>
                      )}
                      {subscription.quantity && (
                        <div className="subscription-quantity">Quantity: {subscription.quantity}</div>
                      )}
                      {subscription.startDate && (
                        <div className="subscription-dates">
                          Start: {formatDate(subscription.startDate)}
                          {subscription.endDate && ` | End: ${formatDate(subscription.endDate)}`}
                        </div>
                      )}
                      {subscription.commitmentEndDate && (
                        <div className="subscription-commitment">
                          Commitment End: {formatDate(subscription.commitmentEndDate)}
                        </div>
                      )}
                      {subscription.serial && (
                        <div className="subscription-serial">Serial: {subscription.serial}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* User Timeline Section */}
        <div className="analytics-section timeline">
          <div className="section-header">
            <h3>Licensed User Registration Timeline</h3>
            <p>Users with assigned licenses created over time based on registration date</p>
          </div>
          <div className="section-content">
            {analyticsData.userTimeline.length === 0 ? (
              <div className="no-data">No user timeline data available</div>
            ) : (
              <div className="user-timeline-graph">
                <div className="graph-container">
                  <div className="y-axis">
                    {(() => {
                      const maxCount = Math.max(...analyticsData.userTimeline.map(e => e.count));
                      const yAxisValues = [];
                      const step = Math.max(1, Math.ceil(maxCount / 5));
                      
                      for (let i = 0; i <= maxCount; i += step) {
                        yAxisValues.push(i);
                      }
                      if (yAxisValues[yAxisValues.length - 1] !== maxCount) {
                        yAxisValues.push(maxCount);
                      }
                      
                      return yAxisValues.map((value, index) => (
                        <div key={index} className="y-axis-label" style={{ bottom: `${(value / maxCount) * 100}%` }}>
                          {value}
                        </div>
                      ));
                    })()}
                  </div>
                  
                  <div className="chart-area">
                    <div className="bars-container">
                      {analyticsData.userTimeline.map((entry, index) => {
                        const maxCount = Math.max(...analyticsData.userTimeline.map(e => e.count));
                        const height = maxCount > 0 ? (entry.count / maxCount) * 100 : 0;
                        
                        return (
                          <div key={index} className="chart-bar-container">
                            <div 
                              className="chart-bar"
                              style={{ 
                                height: `${height}%`,
                                backgroundColor: `hsl(${200 + (height * 1.6)}, 70%, 60%)`
                              }}
                              title={`${entry.count} user${entry.count !== 1 ? 's' : ''} on ${new Date(entry.date).toLocaleDateString()}`}
                            ></div>
                            <div className="x-axis-label">
                              {new Date(entry.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Grid lines */}
                    <div className="grid-lines">
                      {(() => {
                        const maxCount = Math.max(...analyticsData.userTimeline.map(e => e.count));
                        const step = Math.max(1, Math.ceil(maxCount / 5));
                        const gridLines = [];
                        
                        for (let i = 0; i <= maxCount; i += step) {
                          gridLines.push(
                            <div 
                              key={i} 
                              className="grid-line" 
                              style={{ bottom: `${(i / maxCount) * 100}%` }}
                            ></div>
                          );
                        }
                        return gridLines;
                      })()}
                    </div>
                  </div>
                </div>
                
                <div className="graph-legend">
                  <div className="legend-item">
                    <div className="legend-color"></div>
                    <span>Licensed Users Created</span>
                  </div>
                </div>
                
                <div className="graph-stats">
                  {(() => {
                    if (analyticsData.userTimeline.length === 0) return null;
                    
                    // Calculate monthly average
                    const totalUsers = analyticsData.userTimeline.reduce((sum, entry) => sum + entry.count, 0);
                    const dates = analyticsData.userTimeline.map(entry => new Date(entry.date));
                    const minDate = new Date(Math.min(...dates));
                    const maxDate = new Date(Math.max(...dates));
                    const monthsDiff = (maxDate.getFullYear() - minDate.getFullYear()) * 12 + (maxDate.getMonth() - minDate.getMonth()) + 1;
                    const avgPerMonth = monthsDiff > 0 ? (totalUsers / monthsDiff).toFixed(1) : totalUsers.toFixed(1);
                    
                    return (
                      <div className="stats-container">
                        <div className="stat-item">
                          <span className="stat-label">Average per month:</span>
                          <span className="stat-value">{avgPerMonth} users</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Total licensed users:</span>
                          <span className="stat-value">{totalUsers}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Time period:</span>
                          <span className="stat-value">
                            {minDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {maxDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Device Lifecycle Section */}
        <div className="analytics-section lifecycle">
          <div className="section-header">
            <h3>Lifecycle Forecast</h3>
            <p>Warranty and commitment expiration timeline for the next 6 months</p>
          </div>
          <div className="section-content">
            {analyticsData.deviceLifecycle.length === 0 ? (
              <div className="no-data">No device lifecycle data available</div>
            ) : (
              <div className="lifecycle-timeline">
                {analyticsData.deviceLifecycle.map((device, index) => {
                  // Check both warranty-end and commitmentEndDate
                  const warrantyDays = getDaysUntilExpiry(device['warranty-end']);
                  const commitmentDays = getDaysUntilExpiry(device.commitmentEndDate);
                  
                  // Use the earliest expiring date for status calculation
                  const earliestExpiry = warrantyDays !== null && commitmentDays !== null 
                    ? Math.min(warrantyDays, commitmentDays)
                    : warrantyDays !== null ? warrantyDays : commitmentDays;
                  
                  const status = getLifecycleStatus(earliestExpiry);
                  
                  return (
                    <div key={index} className="lifecycle-item">
                      <div className="device-info">
                        <div className="device-name">{device.name || device.model || 'Unknown Device'}</div>
                        <div className="device-details">
                          <span>Vendor: {device.manufacturer || 'N/A'}</span>
                          <span>Model: {device.model || 'N/A'}</span>
                          {device.serial && device.serial !== 'N/A' && <span>Serial: {device.serial}</span>}
                          {device.isCloudfactorySubscription && device.billingCycle && (
                            <span className="subscription-badge">{device.billingCycle} Billing</span>
                          )}
                        </div>
                      </div>
                      <div className="expiry-info">
                        {device['warranty-end'] && (
                          <div className="warranty-info">
                            <div className="warranty-date">
                              Warranty Expires: {formatDate(device['warranty-end'])}
                            </div>
                            {warrantyDays !== null && (
                              <div className="days-remaining">
                                {warrantyDays < 0 ? 'Expired' : `${warrantyDays} days remaining`}
                              </div>
                            )}
                          </div>
                        )}
                        {device.commitmentEndDate && (
                          <div className="commitment-info">
                            <div className="commitment-date">
                              Commitment Expires: {formatDate(device.commitmentEndDate)}
                            </div>
                            {commitmentDays !== null && (
                              <div className="days-remaining">
                                {commitmentDays < 0 ? 'Expired' : `${commitmentDays} days remaining`}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="lifecycle-status" style={{ color: getStatusColor(status) }}>
                          Status: {getStatusLabel(status)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* User Cleanup Suggestions Section */}
        <div className="analytics-section cleanup-suggestions">
          <div className="section-header">
            <h3>User Cleanup Suggestions</h3>
            <p>Users with no activity in OneDrive or Mailbox for more than 60 days</p>
          </div>
          <div className="section-content">
            {cleanupSuggestions.onedrive.length === 0 && cleanupSuggestions.mailbox.length === 0 ? (
              <div className="no-data">No inactive users found - All users have recent activity!</div>
            ) : (
              <div className="cleanup-grid">
                {/* OneDrive Inactive Users */}
                {cleanupSuggestions.onedrive.length > 0 && (
                  <div className="cleanup-category">
                    <h4 className="cleanup-category-title">
                      <span className="cleanup-icon">☁️</span>
                      Inactive OneDrive Users ({cleanupSuggestions.onedrive.length})
                    </h4>
                    <div className="cleanup-list">
                      {cleanupSuggestions.onedrive.map((user, index) => {
                        const daysSince = getDaysSinceActivity(user.OnedriveLastActivity);
                        const ticketKey = `onedrive-${user.userPrincipalName || index}`;
                        const isCreating = creatingTicketFor === ticketKey;
                        const feedback = ticketCreateFeedback[ticketKey];
                        return (
                          <div key={`onedrive-${index}`} className="cleanup-item onedrive">
                            <div className="cleanup-user-info">
                              <div className="cleanup-user-name">{user.userPrincipalName}</div>
                              <div className="cleanup-details">
                                <span>Storage Used: {user.OnedriveProcent || 0}%</span>
                                {user.OnedriveStorageUsed && (
                                  <span>{formatBytes(user.OnedriveStorageUsed)}</span>
                                )}
                              </div>
                            </div>
                            <div className="cleanup-status-badge">
                              <div className="cleanup-activity-info onedrive">
                                <div className="cleanup-activity-date">
                                  OneDrive Inactive: {formatDate(user.OnedriveLastActivity)}
                                </div>
                                {daysSince !== null && (
                                  <div className="cleanup-activity-days">
                                    {daysSince} days ago
                                  </div>
                                )}
                              </div>
                              <div className="cleanup-activity-status onedrive-badge">
                                Status: Inactive
                              </div>
                              <div className="cleanup-ticket-actions">
                                <button
                                  type="button"
                                  className="cleanup-create-ticket-btn"
                                  onClick={() => createCleanupTicket(user, 'onedrive')}
                                  disabled={isCreating}
                                >
                                  {isCreating ? 'Creating…' : 'Create a support ticket'}
                                </button>
                                {feedback && (
                                  <div className={`cleanup-ticket-feedback ${feedback.success ? 'success' : 'error'}`}>
                                    {feedback.success && feedback.ticketUrl ? (
                                      <a href={feedback.ticketUrl} target="_blank" rel="noopener noreferrer">
                                        {feedback.message}
                                      </a>
                                    ) : (
                                      feedback.message
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Mailbox Inactive Users */}
                {cleanupSuggestions.mailbox.length > 0 && (
                  <div className="cleanup-category">
                    <h4 className="cleanup-category-title">
                      <span className="cleanup-icon">📧</span>
                      Inactive Mailbox Users ({cleanupSuggestions.mailbox.length})
                    </h4>
                    <div className="cleanup-list">
                      {cleanupSuggestions.mailbox.map((user, index) => {
                        const daysSince = getDaysSinceActivity(user.MailboxLastActivity);
                        const ticketKey = `mailbox-${user.userPrincipalName || index}`;
                        const isCreating = creatingTicketFor === ticketKey;
                        const feedback = ticketCreateFeedback[ticketKey];
                        return (
                          <div key={`mailbox-${index}`} className="cleanup-item mailbox">
                            <div className="cleanup-user-info">
                              <div className="cleanup-user-name">{user.userPrincipalName}</div>
                              <div className="cleanup-details">
                                <span>Mailbox Size: {formatBytes(user.MailboxUsages)}</span>
                                {user.MailboxRecipientType && (
                                  <span className={`mailbox-type ${user.MailboxRecipientType.toLowerCase().includes('shared') ? 'shared-mailbox' : 'user-mailbox'}`}>
                                    Type: {user.MailboxRecipientType}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="cleanup-status-badge">
                              <div className="cleanup-activity-info mailbox">
                                <div className="cleanup-activity-date">
                                  Mailbox Inactive: {formatDate(user.MailboxLastActivity)}
                                </div>
                                {daysSince !== null && (
                                  <div className="cleanup-activity-days">
                                    {daysSince} days ago
                                  </div>
                                )}
                              </div>
                              <div className="cleanup-activity-status mailbox-badge">
                                Status: Inactive
                              </div>
                              <div className="cleanup-ticket-actions">
                                <button
                                  type="button"
                                  className="cleanup-create-ticket-btn"
                                  onClick={() => createCleanupTicket(user, 'mailbox')}
                                  disabled={isCreating}
                                >
                                  {isCreating ? 'Creating…' : 'Create a support ticket'}
                                </button>
                                {feedback && (
                                  <div className={`cleanup-ticket-feedback ${feedback.success ? 'success' : 'error'}`}>
                                    {feedback.success && feedback.ticketUrl ? (
                                      <a href={feedback.ticketUrl} target="_blank" rel="noopener noreferrer">
                                        {feedback.message}
                                      </a>
                                    ) : (
                                      feedback.message
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
